import React, { useContext, ComponentType, useCallback } from 'react';
import Typography from '@material-ui/core/Typography';

import { ProductionSite } from '../../models';
import { EvidenceAPI } from '../../enums';

import { fetchProductionSite } from './production-sites-api';
import Button from '@material-ui/core/Button';
import { DetailContext } from '@eas/common-web';
import {
  MapEvidenceContext,
  MapEvidenceView,
} from '../../components/evidence/map-evidence/map-context';
import ButtonGroup from '@material-ui/core/ButtonGroup/ButtonGroup';

import { useMarkerPopupFactory } from '../../components/map/hook/marker-popup-factory';
import {
  CrzpMarker,
  MarkerType,
  MarkerProps,
} from '../../components/map/map-types';
import { MapContainer } from '../../components/map/map-container';
import { RemoteMarkers } from '../../components/map/remote-markers';
import {
  createMarkerIcon,
  createLeafletMarker,
} from '../../components/map/marker';

export function ProductionSiteMarkerTitle({ name }: { name: string }) {
  return (
    <>
      <Typography variant="body2">
        Název průmyslového místa: <b>{name}</b>
      </Typography>
    </>
  );
}

export function ProductionSiteMarkerBody({
  data,
  setActive,
  Actions,
}: {
  data?: ProductionSite;
  setActive: (id: string) => void;
  Actions?: ComponentType<{ data: ProductionSite }>;
}) {
  return (
    <>
      <Typography component="div" variant="body2">
        <>
          <br />
          Souřadnice:
          <p>
            Zeměpisná šířka <b>{data?.wgs84Coordinates?.lat}</b>
          </p>
          <p>
            Zeměpisná délka <b>{data?.wgs84Coordinates?.lon}</b>
          </p>
        </>
      </Typography>
      <br />
      <ButtonGroup variant="contained">
        <Button
          size="small"
          variant="contained"
          onClick={() => setActive(data!.id)}
        >
          Spravovat
        </Button>
        {Actions && data && <Actions data={data} />}
      </ButtonGroup>
      <br />
      <br />
    </>
  );
}

export const ProductionSitePopup = ({
  data,
  setActive,
  Actions,
}: {
  data: ProductionSite;
  marker: CrzpMarker;
  setActive: (id: string) => void;
  Actions?: ComponentType<{ data: ProductionSite }>;
}) => {
  return (
    <div
      style={{
        backgroundColor: 'white',
        color: 'black',
      }}
    >
      <ProductionSiteMarkerTitle name={data?.name ?? ''} />
      <ProductionSiteMarkerBody
        data={data!}
        setActive={() => {
          setActive(data.id);
        }}
        Actions={Actions}
      />
    </div>
  );
};

const inactiveIcon = createMarkerIcon(MarkerType.FILLED_GREY, 'small');
const activeIcon = createMarkerIcon(MarkerType.FILLED_BLUE, 'small');
const selectedIconActive = createMarkerIcon(MarkerType.BIG_RED, 'medium');
const selectedIconInactive = createMarkerIcon(MarkerType.BIG_GREY, 'medium');

export function ProductionSitesMap() {
  const { setView } = useContext(MapEvidenceContext);
  const { setActive, onPersisted, source } = useContext(DetailContext);

  const activateProductionSite = (id: string) => {
    setActive(id);
    onPersisted(id);
    setView(MapEvidenceView.DETAIL);
  };

  const config = useMarkerPopupFactory<ProductionSite>({
    fetchData: (marker) => fetchProductionSite(marker.id ?? '')?.json(),
    PopupBodyComponent: (props) => {
      return ProductionSitePopup({
        ...props,
        setActive: activateProductionSite,
      });
    },
  });

  const customCreateLeafletMarker = useCallback(
    (props: MarkerProps) => {
      let customIcon: any = {};
      const markerLeafletOptions = {} as L.MarkerOptions;
      if (source.data?.id === props.id) {
        customIcon = props.active ? selectedIconActive : selectedIconInactive;
        markerLeafletOptions.title = props.active ? 'Aktivní' : 'Neaktivní';
      } else if (props.active) {
        customIcon = activeIcon;
        markerLeafletOptions.title = 'Aktivní';
      } else {
        customIcon = inactiveIcon;
        markerLeafletOptions.title = 'Neaktivní';
      }

      return createLeafletMarker({
        ...props,
        customIcon,
        markerLeafletOptions,
      });
    },
    [source.data?.id]
  );
  return (
    <MapContainer showSuggest>
      <RemoteMarkers
        api={EvidenceAPI.PRODUCTION_SITES}
        markerPopupConfig={config}
        createLeafletMarker={customCreateLeafletMarker}
      />
    </MapContainer>
  );
}
